/**
 * 頁 7 — 出庫單列表（`ui-spec` 頁 7）
 *
 * 四方入面**只有物流／職員／管理員**見到呢頁（供應商唔參與出庫流程；後端 `guard()` 一樣擋）。
 * 篩選：狀態／類型 —— 兩個都係後端契約值（模組 `OUTBOUND_STATUSES`／`OUTBOUND_KINDS`）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import EmptyState from '@coeldery/empty-state';
import FormSelectField from '@coeldery/form-select-field';
import { btnPrimary, card, hint as hintStyle } from '@coeldery/ui-kit';
import {
  OutboundStatusChip,
  OUTBOUND_KINDS,
  OUTBOUND_STATUSES,
  destinationLabel,
  kindLabelKey,
  modeLabelKey,
  statusLabelKey,
} from '../components/OutboundStatusChip';
import { ApiError, api, type Me, type OutboundKind, type OutboundOrder, type OutboundStatus } from '../lib/api';

export default function Outbound({ user }: { user: Me }) {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<OutboundStatus | ''>('');
  const [kind, setKind] = useState<OutboundKind | ''>('');
  const [orders, setOrders] = useState<OutboundOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res = await api.outboundOrders({ status, kind });
      setOrders(res.orders ?? []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [status, kind, t]);

  useEffect(() => {
    void load();
  }, [load]);

  /** 開單只有職員／管理員（後端一樣擋；呢度只係唔顯示做唔到嘅掣） */
  const canCreate = user.role === 'staff' || user.role === 'admin';

  const statusOptions = [
    { value: '', label: t('out.allStatus') },
    ...OUTBOUND_STATUSES.map((s) => ({ value: s as string, label: t(statusLabelKey(s)) })),
  ];
  const kindOptions = [
    { value: '', label: t('out.allKinds') },
    ...OUTBOUND_KINDS.map((k) => ({ value: k as string, label: t(kindLabelKey(k)) })),
  ];

  return (
    <>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800 }}>{t('out.title')}</h2>
        {canCreate && (
          <Link
            to="/outbound/new"
            style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
          >
            {t('out.newOrder')}
          </Link>
        )}
      </div>

      <div style={{ display: 'grid', gap: '12px', margin: '12px 0', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
        <FormSelectField
          id="f-out-status"
          labelKey="out.filterStatus"
          value={status}
          onChange={(v) => setStatus(v as OutboundStatus | '')}
          options={statusOptions}
        />
        <FormSelectField
          id="f-out-kind"
          labelKey="out.filterKind"
          value={kind}
          onChange={(v) => setKind(v as OutboundKind | '')}
          options={kindOptions}
        />
      </div>

      {loading && <p style={hintStyle}>{t('common.loading')}</p>}
      {error && (
        <p style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 700 }} role="alert">
          {error}{' '}
          <button
            type="button"
            onClick={() => void load()}
            style={{ border: 'none', background: 'none', color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
          >
            {t('common.refresh')}
          </button>
        </p>
      )}

      {!loading && orders.length === 0 && (
        <EmptyState titleKey="out.emptyTitle" bodyKey="out.emptyBody" />
      )}

      {orders.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {orders.map((o) => (
            <li key={o.id} style={{ ...card, padding: 0 }}>
              <Link
                to={`/outbound/${o.id}`}
                style={{ display: 'block', padding: '14px 16px', textDecoration: 'none', color: 'inherit' }}
              >
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>{o.orderNo}</strong>
                  <OutboundStatusChip status={o.status} />
                  {/* ⭐ 待交收（`acknowledged`）—— 列表一眼睇到「呢張單等緊客人嚟攞／送達」 */}
                  {o.status === 'acknowledged' && (
                    <span style={{ ...hintStyle, color: 'var(--color-accent)', fontWeight: 800 }}>
                      {t('out.awaitingHandover')}
                    </span>
                  )}
                  <span style={hintStyle}>{t(kindLabelKey(o.outboundKind))}</span>
                  <span style={hintStyle}>{t(modeLabelKey(o.deliveryMode))}</span>
                </div>
                <p style={hintStyle}>
                  {t('out.destination')}：
                  {destinationLabel(
                    { labelZhHk: o.destinationLabelZhHk, labelEn: o.destinationLabelEn },
                    i18n.language,
                  )}
                  {o.consigneeName ? ` · ${t('out.consignee')}：${o.consigneeName}` : ''}
                  {o.deliveryEvidence?.missing ? ` · ⚠️ ${t('out.missingEvidenceShort')}` : ''}
                </p>
                <p style={hintStyle}>
                  {t('out.pickupAt')}：{o.pickupAt ?? '—'}
                  {o.deliveryCode ? ` · ${t('out.deliveryCode')}：${o.deliveryCode}` : ''}
                  {o.outboundCbm > 0 ? ` · ${t('out.outboundCbm')}：${o.outboundCbm}` : ''}
                </p>
                {o.pickDiscrepancy && (
                  <p style={{ ...hintStyle, color: 'var(--color-accent)' }}>
                    {t('out.pickDiscrepancy')}：{o.pickDiscrepancy}
                  </p>
                )}
              </Link>
              {/* ⭐ 待交收 → 一粒掣入去撳（2026-09-28 產品負責人：「i really can't see the button」） */}
              {o.status === 'acknowledged' && (
                <div style={{ padding: '0 16px 14px' }}>
                  <Link
                    to={`/outbound/${o.id}`}
                    style={{ ...btnPrimary, display: 'inline-flex', alignItems: 'center', textDecoration: 'none' }}
                  >
                    {t('out.goHandOver')}
                  </Link>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
